import { useLayoutEffect, useRef } from 'react'
import { gsap, registerGsap } from '../../anim/gsapSetup'
import { useReducedMotion } from '../../anim/useReducedMotion'
import { Section } from '../common/Section'
import { Eyebrow } from '../common/Eyebrow'
import { SplitHeading } from '../animation/SplitHeading'
import { Icon } from '../common/Icon'

const STEPS = [
  { icon: 'Microscope', title: 'Understand the soil', body: 'Start with soil biology — what the field has, and what it’s missing.' },
  { icon: 'Sprout', title: 'Match to the crop', body: 'Pick products from crop-specific programmes built for real Indian conditions.' },
  { icon: 'Droplets', title: 'Mix & apply', body: 'Follow clear dosage, incubation and application steps for each product.' },
  { icon: 'Wheat', title: 'Grow resilient crops', body: 'Rebuild soil, strengthen plants and support quality — season after season.' },
]

export function ApplicationJourney() {
  const scopeRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useLayoutEffect(() => {
    const scope = scopeRef.current
    if (!scope || reduced) return
    registerGsap()
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '[data-journey="line"]',
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: 'none',
          scrollTrigger: { trigger: scope, start: 'top 65%', end: 'bottom 75%', scrub: true },
        },
      )
      gsap.from('[data-journey="step"]', {
        opacity: 0,
        y: 30,
        stagger: 0.15,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: { trigger: scope, start: 'top 70%' },
      })
    }, scope)
    return () => ctx.revert()
  }, [reduced])

  return (
    <Section className="grain relative overflow-hidden bg-night py-section text-white">
      <div className="container-x">
        <div className="max-w-2xl">
          <Eyebrow tone="light">How it works</Eyebrow>
          <SplitHeading
            as="h2"
            text="From living soil to a stronger harvest."
            className="mt-4 font-display text-h1 font-semibold text-white"
          />
          <p className="mt-4 text-lead text-white/70">
            A simple path that farmers and retailers follow — from understanding the soil to a healthier crop.
          </p>
        </div>

        <div ref={scopeRef} className="relative mt-16">
          {/* connecting line (desktop) */}
          <div className="pointer-events-none absolute left-0 right-0 top-8 hidden h-px bg-white/15 lg:block">
            <div data-journey="line" className="h-full origin-left bg-brand-400" />
          </div>

          <ol className="grid gap-10 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <li key={s.title} data-journey="step" className="relative">
                <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full border border-white/15 bg-night-700 text-brand-300 shadow-glow">
                  <Icon name={s.icon} className="h-7 w-7" />
                </span>
                <span className="mt-5 block font-display text-sm font-semibold uppercase tracking-[0.18em] text-brand-300">
                  Step {i + 1}
                </span>
                <h3 className="mt-2 font-display text-xl font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-[0.95rem] leading-relaxed text-white/65">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Section>
  )
}
